import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import axios from 'axios';
// Create Auth Context
const AuthContext = createContext(null);

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Auth Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    console.log('AuthProvider: Initializing auth...');
    initializeAuth();
  }, []);

  const initializeAuth = () => {
    console.log('AuthProvider: initializeAuth called');
    try {
      const userData = authService.getUserData();
      const token = authService.getToken();
      const isExpired = authService.isTokenExpired();
      
      console.log('AuthProvider: Auth state check', {
        hasUserData: !!userData,
        hasToken: !!token,
        isExpired: isExpired,
        currentPath: window.location.pathname
      });
      
      if (userData && token && !isExpired) {
        console.log('AuthProvider: User is authenticated');
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        console.log('AuthProvider: User not authenticated, clearing state');
        // Don't call logout here to avoid redirect loop
        // Just clear the state silently
        setUser(null);
        setIsAuthenticated(false);
        
        // Clear invalid/expired data without redirect
        if (isExpired || (!userData && token)) {
          console.log('AuthProvider: Clearing expired/invalid data');
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('username');
          localStorage.removeItem('orgId');
          localStorage.removeItem('appId');
          localStorage.removeItem('labTypeId');
          localStorage.removeItem('userTypeId');
          localStorage.removeItem('isSubUser');
          localStorage.removeItem('mainOrgId');
          localStorage.removeItem('mainAppId');
          localStorage.removeItem('subUserOrgId');
          localStorage.removeItem('disciplineId');
          localStorage.removeItem('disciplineIds');
        }
      }
    } catch (error) {
      console.error('AuthProvider: Error initializing auth:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      console.log('AuthProvider: Initialization complete, setting loading to false');
      setLoading(false);
    }
  };

 const login = async (credentials) => {
  try {
    setLoading(true);

    // Clear old session data before new login
    console.log('AuthContext: Clearing old session before new login');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    localStorage.removeItem('username');
    localStorage.removeItem('orgId');
    localStorage.removeItem('appId');
    localStorage.removeItem('labTypeId');
    localStorage.removeItem('userTypeId');
    localStorage.removeItem('isSubUser');
    localStorage.removeItem('mainOrgId');
    localStorage.removeItem('mainAppId');
    localStorage.removeItem('subUserOrgId');
    localStorage.removeItem('disciplineId');
    localStorage.removeItem('disciplineIds');

    const response = await axios.post(
      // 'https://nablwpnew.qci.org.in/reactApi/api/auth/login',
      `${process.env.REACT_APP_API_URL}/auth/login`,
      credentials
    );

    if (response.data.success) {
      const {
        username,
        orgId,
        appId,
        labTypeId,
        userTypeId,
        token,
        redirectTo,
        passwordChangeRequired,
        isSubUser,
        mainOrgId,
        disciplineId,
        disciplineIds, // New: array of discipline IDs
        subUserOrgId,
      } = response.data.data;

      // 🔹 Store authentication token
      if (token) {
        localStorage.setItem("authToken", token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      // 🔹 For sub-users, store main org's orgId and appId
      // For regular users, store their own orgId and appId
      const effectiveOrgId = isSubUser && mainOrgId ? mainOrgId : orgId;
      const effectiveAppId = appId || effectiveOrgId;

      // 🔹 Store user data in localStorage with FRESH login time
      const userData = {
        username,
        orgId: effectiveOrgId, // Main org's ID for sub-users
        appId: effectiveAppId, // Main org's appId for sub-users (already set by backend)
        labTypeId,
        userTypeId,
        isSubUser: isSubUser || false,
        mainOrgId: mainOrgId || null,
        subUserOrgId: subUserOrgId || null, // Sub-user's own orgId
        disciplineId: disciplineId || null, // Single discipline for backward compatibility
        disciplineIds: disciplineIds || [], // Array of all disciplines
        loginTime: new Date().toISOString(), // FRESH login time for new session
        redirectTo: redirectTo || "/dashboard",
        passwordChangeRequired: passwordChangeRequired || false,
      };

      localStorage.setItem("userData", JSON.stringify(userData));

      // 🔹 Store individual items for easy access
      // For sub-users: use main org's orgId and appId
      localStorage.setItem("username", username);
      localStorage.setItem("orgId", effectiveOrgId?.toString() || "");
      localStorage.setItem("appId", effectiveAppId?.toString() || "");
      localStorage.setItem("labTypeId", labTypeId?.toString() || "");
      localStorage.setItem("userTypeId", userTypeId?.toString() || "");
      
      // Store sub-user specific data
      if (isSubUser) {
        localStorage.setItem("isSubUser", "true");
        localStorage.setItem("mainOrgId", mainOrgId?.toString() || "");
        localStorage.setItem("mainAppId", effectiveAppId?.toString() || ""); // Same as appId for sub-users
        localStorage.setItem("subUserOrgId", subUserOrgId?.toString() || "");
        
        // Store disciplineId (first one for backward compatibility)
        if (disciplineId) {
          localStorage.setItem("disciplineId", disciplineId?.toString() || "");
        }
        
        // Store disciplineIds as JSON array
        if (disciplineIds && disciplineIds.length > 0) {
          localStorage.setItem("disciplineIds", JSON.stringify(disciplineIds));
        }
        
        console.log("✅ Sub-user login - Stored main org credentials:", {
          username,
          mainOrgId,
          mainAppId: effectiveAppId,
          subUserOrgId,
          disciplineId,
          disciplineIds,
          storedOrgId: effectiveOrgId,
          storedAppId: effectiveAppId,
          loginTime: userData.loginTime
        });
      } else {
        // Clear sub-user flags for regular users
        localStorage.removeItem("isSubUser");
        localStorage.removeItem("mainOrgId");
        localStorage.removeItem("mainAppId");
        localStorage.removeItem("subUserOrgId");
        localStorage.removeItem("disciplineId");
        localStorage.removeItem("disciplineIds");
        
        console.log("✅ Regular user login - Session started at:", userData.loginTime);
      }

      // Sync authService singleton with fresh session data to prevent stale
      // in-memory loginTime from triggering false session expiry on next load
      authService.token = token;
      authService.userData = userData;
      authService.stopSessionTimeout();
      authService.startSessionTimeout();

      // 🔹 Update state
      setUser(response.data.data);
      setIsAuthenticated(true);

      return response.data; // Return only the useful part
    } else {
      throw new Error(response.data.message || "Login failed");
    }
  } catch (error) {
    console.error("Login error:", error);
    setUser(null);
    setIsAuthenticated(false);
    throw error;
  } finally {
    setLoading(false);
  }
};
  const loginAuto = async (credentials) => {
    try {
      setLoading(true);
      const response = await authService.loginAuto(credentials);
      
      if (response.success) {
        setUser(response.data);
        setIsAuthenticated(true);
        return response;
      } else {
        throw new Error('Login failed');
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Call logout with redirect enabled when user explicitly logs out
    authService.logout(true);
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setIsAuthenticated(false);
  };

  const changePassword = async (passwordData) => {
    try {
      const response = await authService.changePassword(passwordData);
      if (response.success) {
        setUser(response.data);
        return response;
      } else {
        throw new Error('Password change failed');
      }
    } catch (error) {
      console.error('Password change error:', error);
      throw error;
    }
  };

  const updateUser = (userData) => {
    authService.updateUserData(userData);
    setUser({ ...user, ...userData });
  };

  // Get user info methods
  const getUsername = () => authService.getUsername();
  const getOrgId = () => authService.getOrgId();
  const getAppId = () => authService.getAppId();
  const getLabTypeId = () => authService.getLabTypeId();
  const getUserTypeId = () => authService.getUserTypeId();
  const isSubUser = () => authService.isSubUser();
  const getMainOrgId = () => authService.getMainOrgId();
  const getRedirectUrl = () => authService.getRedirectUrl();

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    loginAuto,
    logout,
    changePassword,
    updateUser,
    getUsername,
    getOrgId,
    getAppId,
    getLabTypeId,
    getUserTypeId,
    isSubUser,
    getMainOrgId,
    getRedirectUrl,
    // Helper methods
    hasRole: (role) => user?.userTypeId === role,
    isLabUser: () => !user?.userTypeId || user?.userTypeId === '' || user?.userTypeId === '0'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};